import { Box, Link } from '@mui/material'
import { MRT_ColumnDef as MRTColumnDef } from 'material-react-table'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import MRTDataGridv2 from '../../../components/MRTDataGrid/MRTDataGridv2'
import { messages } from '../messages'
import { UnassignedWaypoint } from '../types'
import EmbeddedContainer from './EmbeddedContainer'
import EmbeddedTransitDetailsTable from './EmbeddedTransitDetailsTable'

type EmbeddedWaypointsTableProps = {
  waypoints: UnassignedWaypoint[]
  onServicesClick?: (waypoint: UnassignedWaypoint) => void
}

const EmbeddedWaypointsTable = ({ waypoints, onServicesClick }: EmbeddedWaypointsTableProps) => {
  const { formatMessage } = useIntl()

  const columns = useMemo<MRTColumnDef<UnassignedWaypoint>[]>(() => {
    return [
      {
        accessorFn: (row) => row.waypointTypeName ?? formatMessage(messages.notAvailable),
        header: formatMessage(messages.waypoint),
      },
      {
        accessorFn: (row) => row.locationName ?? formatMessage(messages.notAvailable),
        header: formatMessage(messages.location),
      },
      {
        accessorFn: (row) => row.contactName ?? formatMessage(messages.notAvailable),
        header: formatMessage(messages.contact),
      },
      {
        accessorFn: (row) => row.instructions ?? formatMessage(messages.notAvailable),
        header: formatMessage(messages.specialInstructions),
      },
      {
        accessorFn: (row) =>
          row.clearingRequired ? formatMessage(messages.yes) : formatMessage(messages.no),
        header: formatMessage(messages.clearingRequired),
      },
      {
        accessorFn: (row) => row.services ?? formatMessage(messages.notAvailable),
        header: formatMessage(messages.services),
        Cell: ({ cell, row }: any) => {
          const value = cell.getValue() as string
          const hasServices = value && value !== formatMessage(messages.notAvailable)
          return hasServices && onServicesClick ? (
            <Link
              component="button"
              variant="body2"
              onClick={(e) => {
                e.stopPropagation()
                onServicesClick(row.original)
              }}
              sx={{ cursor: 'pointer', whiteSpace: 'pre-line', textAlign: 'left' }}
            >
              {value}
            </Link>
          ) : (
            <Box sx={{ whiteSpace: 'pre-line' }}>
              {value}
            </Box>
          )
        },
      },
    ]
  }, [formatMessage])

  const sortedWaypoints = useMemo(() => {
    return [...waypoints].sort((a, b) => a.sequence - b.sequence)
  }, [waypoints])

  return (
    <EmbeddedContainer>
      <Box
        sx={{
          width: '100%',
          height: '100%',
        }}
      >
        <MRTDataGridv2
          columns={columns}
          data={sortedWaypoints || []}
          getRowId={(row) => row.waypointId.toString()}
          enableColumnActions={false}
          enableColumnFilters={false}
          enablePagination={false}
          enableSorting={false}
          enableBottomToolbar={false}
          enableTopToolbar={false}
          enableExpandAll={true}
          positionActionsColumn='last'
          renderDetailPanel={({ row }) => {
            const transitDetails = row.original.transitDetails
            if (!transitDetails || transitDetails.length === 0) {
              return null
            }
            return <EmbeddedTransitDetailsTable transitDetails={transitDetails} />
          }}
        />
      </Box>
    </EmbeddedContainer>
  )
}

export default EmbeddedWaypointsTable
